import React, { useState } from 'react';
import { Card } from './ui/Card';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { SearchIcon, HeartIcon, MapPinIcon, StarIcon, FilterIcon, ChevronDownIcon, XIcon, GlobeIcon, BuildingIcon, TagIcon, MailIcon, ExternalLinkIcon, ArrowLeftIcon, InstagramIcon, LinkedinIcon, FacebookIcon, YoutubeIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { exhibitors as exhibitorsData } from '../data/exhibitors';
interface Social {
  logo: string;
  link: string;
}
interface Exhibitor {
  id: string;
  name: string;
  logo: string;
  industries: string[];
  headquarters: string | null;
  events: string[];
  email: string | null;
  socials: Social[] | null;
  sponsor: string | null;
  standNumber: string;
  website: string;
  description: string;
  isFavorite?: boolean;
}
interface ExhibitorListProps {
  onNavigate?: (page: string) => void;
}
export function ExhibitorList({
  onNavigate
}: ExhibitorListProps) {
  const {
    currentEvent
  } = useEvent();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<string>('all');
  const [selectedHeadquarters, setSelectedHeadquarters] = useState<string>('all');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedExhibitor, setSelectedExhibitor] = useState<Exhibitor | null>(null);
  // Use imported exhibitors data
  const [exhibitors] = useState<Exhibitor[]>(exhibitorsData);
  // Get social media icon component
  const getSocialIcon = (logo: string) => {
    const iconClass = 'w-5 h-5';
    switch (logo.toLowerCase()) {
      case 'instagram':
        return <InstagramIcon className={iconClass} data-id="element-3410" />;
      case 'linkedin':
        return <LinkedinIcon className={iconClass} data-id="element-3411" />;
      case 'facebook':
        return <FacebookIcon className={iconClass} data-id="element-3412" />;
      case 'youtube':
        return <YoutubeIcon className={iconClass} data-id="element-3413" />;
      default:
        return <GlobeIcon className={iconClass} data-id="element-3414" />;
    }
  };
  // Get unique values for filters
  const eventOptions = ['all', 'DAI', 'DAS', 'DMS'];
  const headquartersOptions = ['all', ...Array.from(new Set(exhibitors.map(e => e.headquarters).filter(Boolean))).sort()];
  const industryOptions = ['all', ...Array.from(new Set(exhibitors.flatMap(e => e.industries))).sort()];
  // Map event codes to names
  const eventNames: Record<string, string> = {
    DAI: 'Dubai Active Industry',
    DAS: 'Dubai Active Show',
    DMS: 'Dubai Muscle Show'
  };
  const filteredExhibitors = exhibitors.filter(exhibitor => {
    const matchesSearch = exhibitor.name.toLowerCase().includes(searchQuery.toLowerCase()) || exhibitor.standNumber.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEvent = selectedEvent === 'all' || exhibitor.events.includes(selectedEvent);
    const matchesHeadquarters = selectedHeadquarters === 'all' || exhibitor.headquarters === selectedHeadquarters;
    const matchesIndustry = selectedIndustry === 'all' || exhibitor.industries.includes(selectedIndustry);
    return matchesSearch && matchesEvent && matchesHeadquarters && matchesIndustry;
  }).sort((a, b) => {
    // Priority sorting for sponsors
    const getSponsorPriority = (sponsor: string | null): number => {
      if (!sponsor) return 999;
      const lower = sponsor.toLowerCase();
      if (lower.includes('platinum')) return 1;
      if (lower.includes('gold')) return 2;
      if (lower.includes('silver')) return 3;
      return 4;
    };
    const priorityA = getSponsorPriority(a.sponsor);
    const priorityB = getSponsorPriority(b.sponsor);
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    // Then alphabetically by name
    return a.name.localeCompare(b.name);
  });
  // Exhibitor Profile View
  if (selectedExhibitor) {
    return <div className="pb-24" data-id="element-3415">
        {/* Profile Content */}
        <div className="p-4 space-y-6" data-id="element-3416">
          {/* Logo and Name */}
          <div className="flex items-start gap-4" data-id="element-3417">
            <img src={selectedExhibitor.logo} alt={selectedExhibitor.name} className="w-24 h-24 rounded-lg object-cover" data-id="element-3418" />
            <div className="flex-1" data-id="element-3419">
              <h1 className="text-2xl font-bold text-gray-900 font-gotham mb-2" data-id="element-3420">
                {selectedExhibitor.name}
              </h1>
              {selectedExhibitor.sponsor && <div className="mb-2" data-id="element-3421">
                  <Badge variant="warning" size="sm" data-id="element-3422">
                    <StarIcon className="w-3 h-3 mr-1 inline" data-id="element-3423" />
                    {selectedExhibitor.sponsor}
                  </Badge>
                </div>}
              <div className="flex items-center gap-2 text-sm text-gray-600 font-gotham" data-id="element-3424">
                <MapPinIcon className="w-4 h-4" data-id="element-3425" />
                Stand {selectedExhibitor.standNumber}
              </div>
            </div>
          </div>

          {/* Description */}
          <Card padding="md" data-id="element-3426">
            <h3 className="font-bold text-gray-900 font-gotham mb-2" data-id="element-3427">About</h3>
            <p className="text-gray-600 font-gotham" data-id="element-3428">
              {selectedExhibitor.description}
            </p>
          </Card>

          {/* Details */}
          <Card padding="md" data-id="element-3429">
            <h3 className="font-bold text-gray-900 font-gotham mb-3" data-id="element-3430">
              Details
            </h3>
            <div className="space-y-3" data-id="element-3431">
              {selectedExhibitor.headquarters && <div className="flex items-start gap-3" data-id="element-3432">
                  <GlobeIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-3433" />
                  <div data-id="element-3434">
                    <div className="text-xs text-gray-500 font-gotham" data-id="element-3435">
                      Headquarters
                    </div>
                    <div className="text-sm text-gray-900 font-gotham" data-id="element-3436">
                      {selectedExhibitor.headquarters}
                    </div>
                  </div>
                </div>}

              <div className="flex items-start gap-3" data-id="element-3437">
                <BuildingIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-3438" />
                <div data-id="element-3439">
                  <div className="text-xs text-gray-500 font-gotham" data-id="element-3440">
                    Events
                  </div>
                  <div className="text-sm text-gray-900 font-gotham" data-id="element-3441">
                    {selectedExhibitor.events.map(e => eventNames[e] || e).join(', ')}
                  </div>
                </div>
              </div>

              {selectedExhibitor.industries.length > 0 && <div className="flex items-start gap-3" data-id="element-3442">
                  <TagIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-3443" />
                  <div data-id="element-3444">
                    <div className="text-xs text-gray-500 font-gotham mb-1" data-id="element-3445">
                      Industries
                    </div>
                    <div className="flex flex-wrap gap-1" data-id="element-3446">
                      {selectedExhibitor.industries.map((industry, idx) => <Badge key={idx} variant="default" size="sm" data-id="element-3447">
                          {industry}
                        </Badge>)}
                    </div>
                  </div>
                </div>}

              {selectedExhibitor.email && <div className="flex items-start gap-3" data-id="element-3448">
                  <MailIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-3449" />
                  <div data-id="element-3450">
                    <div className="text-xs text-gray-500 font-gotham" data-id="element-3451">
                      Email
                    </div>
                    <a href={`mailto:${selectedExhibitor.email}`} className="text-sm text-blue-600 font-gotham hover:underline" data-id="element-3452">
                      {selectedExhibitor.email}
                    </a>
                  </div>
                </div>}

              <div className="flex items-start gap-3" data-id="element-3453">
                <ExternalLinkIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-3454" />
                <div data-id="element-3455">
                  <div className="text-xs text-gray-500 font-gotham" data-id="element-3456">
                    Website
                  </div>
                  <a href={selectedExhibitor.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 font-gotham hover:underline" data-id="element-3457">
                    {selectedExhibitor.website}
                  </a>
                </div>
              </div>
            </div>
          </Card>

          {/* Social Links */}
          {selectedExhibitor.socials && selectedExhibitor.socials.length > 0 && <Card padding="md" data-id="element-3458">
                <h3 className="font-bold text-gray-900 font-gotham mb-3" data-id="element-3459">
                  Social Media
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3460">
                  {selectedExhibitor.socials.map((social, idx) => <a key={idx} href={social.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium font-gotham transition-all hover:bg-opacity-10 capitalize" style={{
              borderColor: currentEvent.colors.primary,
              color: currentEvent.colors.primary
            }} data-id="element-3461">
                      {getSocialIcon(social.logo)}
                      {social.logo}
                    </a>)}
                </div>
              </Card>}

          {/* Actions */}
          <div className="flex gap-3" data-id="element-3462">
            <button className="flex-1 py-3 rounded-lg text-sm font-medium font-gotham text-white transition-all hover:shadow-md" style={{
            backgroundColor: currentEvent.colors.primary
          }} data-id="element-3463">
              Add to Favorites
            </button>
            <button onClick={() => onNavigate?.('floorplan')} className="flex-1 py-3 rounded-lg text-sm font-medium font-gotham border-2 transition-all hover:bg-opacity-10" style={{
            borderColor: currentEvent.colors.primary,
            color: currentEvent.colors.primary
          }} data-id="element-3464">
              Locate on Floorplan
            </button>
          </div>
        </div>
      </div>;
  }
  // List View
  return <div className="pb-24" data-id="element-3465">
      {/* Search and Filters */}
      <div className="sticky top-[88px] z-30 bg-white border-b border-gray-200 p-4 sm:p-5" data-id="element-3466">
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-3467">
          <div className="relative flex-1" data-id="element-3468">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3469" />
            <Input type="text" placeholder="Search by name or stand number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-11 sm:h-10 text-base sm:text-sm" data-id="element-3470" />
          </div>

          {/* Filter Button */}
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center justify-center sm:justify-start gap-2 px-4 h-11 sm:h-10 rounded-lg border-2 text-sm font-medium font-gotham transition-all shrink-0" style={{
          borderColor: currentEvent.colors.primary,
          color: currentEvent.colors.primary,
          backgroundColor: showFilters ? `${currentEvent.colors.primary}10` : 'transparent'
        }} data-id="element-3471">
            <FilterIcon className="w-4 h-4" data-id="element-3472" />
            Filters
            {(selectedEvent !== 'all' || selectedHeadquarters !== 'all' || selectedIndustry !== 'all') && <Badge variant="warning" size="sm" data-id="element-3473">
                Active
              </Badge>}
          </button>
        </div>

        {/* Filter Dropdowns */}
        {showFilters && <div className="space-y-3 pt-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-3474">
            {/* Event Filter */}
            <div data-id="element-3475">
              <label className="text-xs font-semibold text-gray-500 font-gotham mb-1 block" data-id="element-3476">
                EVENT
              </label>
              <select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm font-gotham h-11 sm:h-10" data-id="element-3477">
                <option value="all" data-id="element-3478">All Events</option>
                {eventOptions.filter(e => e !== 'all').map(event => <option key={event} value={event} data-id="element-3479">
                      {eventNames[event]}
                    </option>)}
              </select>
            </div>

            {/* Headquarters Filter */}
            <div data-id="element-3480">
              <label className="text-xs font-semibold text-gray-500 font-gotham mb-1 block" data-id="element-3481">
                HEADQUARTERS
              </label>
              <select value={selectedHeadquarters} onChange={e => setSelectedHeadquarters(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm font-gotham h-11 sm:h-10" data-id="element-3482">
                {headquartersOptions.map(hq => <option key={hq} value={hq} data-id="element-3483">
                    {hq === 'all' ? 'All Locations' : hq}
                  </option>)}
              </select>
            </div>

            {/* Industry Filter */}
            <div data-id="element-3484">
              <label className="text-xs font-semibold text-gray-500 font-gotham mb-1 block" data-id="element-3485">
                INDUSTRY
              </label>
              <select value={selectedIndustry} onChange={e => setSelectedIndustry(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm font-gotham h-11 sm:h-10" data-id="element-3486">
                {industryOptions.map(industry => <option key={industry} value={industry} data-id="element-3487">
                    {industry === 'all' ? 'All Industries' : industry}
                  </option>)}
              </select>
            </div>

            {/* Clear Filters */}
            {(selectedEvent !== 'all' || selectedHeadquarters !== 'all' || selectedIndustry !== 'all') && <button onClick={() => {
          setSelectedEvent('all');
          setSelectedHeadquarters('all');
          setSelectedIndustry('all');
        }} className="w-full py-3 text-sm font-medium font-gotham text-gray-600 hover:text-gray-900" data-id="element-3488">
                Clear All Filters
              </button>}
          </div>}
      </div>

      {/* Results Count */}
      <div className="px-4 py-3 bg-gray-50" data-id="element-3489">
        <p className="text-sm text-gray-600 font-gotham" data-id="element-3490">
          {filteredExhibitors.length} exhibitor
          {filteredExhibitors.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Exhibitor Cards */}
      <div className="px-4 py-4 space-y-3" data-id="element-3491">
        {filteredExhibitors.map(exhibitor => <Card key={exhibitor.id} padding="md" className="relative cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedExhibitor(exhibitor)} data-id="element-3492">
            <div className="flex gap-4" data-id="element-3493">
              {/* Logo */}
              <img src={exhibitor.logo} alt={exhibitor.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" data-id="element-3494" />

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-3495">
                <h3 className="font-bold text-gray-900 font-gotham mb-1" data-id="element-3496">
                  {exhibitor.name}
                </h3>

                {exhibitor.sponsor && <div className="mb-2" data-id="element-3497">
                    <Badge variant="warning" size="sm" data-id="element-3498">
                      <StarIcon className="w-3 h-3 mr-1 inline" data-id="element-3499" />
                      {exhibitor.sponsor}
                    </Badge>
                  </div>}

                <div className="flex items-center gap-3 mb-2 flex-wrap" data-id="element-3500">
                  <div className="flex items-center gap-1 text-sm text-gray-600 font-gotham" data-id="element-3501">
                    <MapPinIcon className="w-4 h-4" data-id="element-3502" />
                    Stand {exhibitor.standNumber}
                  </div>
                  {exhibitor.headquarters && <Badge variant="default" size="sm" data-id="element-3503">
                      {exhibitor.headquarters}
                    </Badge>}
                </div>

                {exhibitor.industries.length > 0 && <div className="flex flex-wrap gap-1 mb-2" data-id="element-3504">
                    {exhibitor.industries.slice(0, 3).map((industry, idx) => <span key={idx} className="text-xs text-gray-500 font-gotham" data-id="element-3505">
                        {industry}
                        {idx < Math.min(2, exhibitor.industries.length - 1) ? ' •' : ''}
                      </span>)}
                    {exhibitor.industries.length > 3 && <span className="text-xs text-gray-500 font-gotham" data-id="element-3506">
                        +{exhibitor.industries.length - 3} more
                      </span>}
                  </div>}
              </div>
            </div>
          </Card>)}
      </div>
    </div>;
}